import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/utils";
import { Plus, Tag, X, Pause, Play, Copy, Files } from "lucide-react";
import { toast } from "sonner";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export default function DiscountsTab({ courts }) {
  const { t } = useTranslation();
  const [discounts, setDiscounts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadDiscounts = async () => {
    try {
      const r = await api.get("/owner/discounts");
      setDiscounts(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDiscounts(); }, []);

  const emptyForm = () => ({
    label: "", court_ids: [], days: [], time_from: undefined, time_to: undefined,
    type: "percent", value: 10, min_hours: undefined, bulk_tiers: [],
    max_uses: undefined, max_uses_per_user: undefined,
    starts_at: undefined, expires_at: undefined,
    apply_mode: "auto", stack_with_discounts: false,
  });

  const [form, setForm] = useState(emptyForm());

  const startCreate = () => {
    setForm(emptyForm());
    setEditing("new");
  };

  const startEdit = (d) => {
    setForm({ ...d });
    setEditing(d.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(emptyForm());
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/owner/discounts", form);
        toast.success(t("owner.discount_created"));
      } else {
        await api.patch(`/owner/discounts/${editing}`, form);
        toast.success(t("owner.discount_updated"));
      }
      cancelEdit();
      loadDiscounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/owner/discounts/${id}`);
      toast.success(t("owner.discount_deleted"));
      loadDiscounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (d) => {
    try {
      await api.patch(`/owner/discounts/${d.id}`, { active: !d.active });
      loadDiscounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const duplicate = async (d) => {
    const { id, used_count, created_at, ...rest } = d;
    setForm({ ...rest });
    setEditing("new");
  };

  if (loading) {
    return <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>;
  }

  return (
    <div>
      {editing ? (
        <div className="max-w-4xl mx-auto">
          <div className="border border-border bg-card p-8">
            <div className="flex items-center justify-between mb-8">
              <span className="text-sm tracking-[0.2em] uppercase font-bold text-muted-foreground/80">
                {editing === "new" ? t("owner.create_discount") : t("owner.edit_discount")}
              </span>
              <button onClick={cancelEdit} className="btn-outline rounded-sm px-4 py-2 text-xs tracking-widest uppercase font-bold">
                {t("common.cancel")}
              </button>
            </div>

            <div className="space-y-10">
              {/* Section: Mode */}
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-4 font-bold">{t("owner.discount_mode")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`border p-5 cursor-pointer transition-all ${form.apply_mode !== "promo" ? "border-volt bg-volt/5" : "border-border bg-background hover:border-foreground/30"}`}
                    onClick={() => setForm({ ...form, apply_mode: "auto" })}>
                    <div className="flex items-center gap-4">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${form.apply_mode !== "promo" ? "border-volt" : "border-muted-foreground/60"}`}>
                        {form.apply_mode !== "promo" && <div className="w-2.5 h-2.5 rounded-full bg-volt" />}
                      </div>
                      <div>
                        <div className="text-base font-semibold">{t("owner.court_form.discount_auto_apply")}</div>
                        <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{t("owner.court_form.discount_auto_apply_desc")}</div>
                      </div>
                    </div>
                  </div>
                  <div className={`border p-5 cursor-pointer transition-all ${form.apply_mode === "promo" ? "border-yellow-400 bg-yellow-400/5" : "border-border bg-background hover:border-foreground/30"}`}
                    onClick={() => setForm({ ...form, apply_mode: "promo" })}>
                    <div className="flex items-center gap-4">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${form.apply_mode === "promo" ? "border-yellow-400" : "border-muted-foreground/60"}`}>
                        {form.apply_mode === "promo" && <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />}
                      </div>
                      <div>
                        <div className="text-base font-semibold">{t("owner.court_form.discount_promo_code")}</div>
                        <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{t("owner.court_form.discount_promo_code_desc")}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section: Discount Details */}
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-4 font-bold">{t("owner.discount_details")}</div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_code")}</label>
                    <input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SUMMER10" className="w-full max-w-xs bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none uppercase" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_label")}</label>
                    <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={t("owner.court_form.discount_label_placeholder")} className="w-full max-w-md bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_type")}</label>
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none cursor-pointer">
                        <option value="percent">{t("owner.court_form.discount_percent")}</option>
                        <option value="flat">{t("owner.court_form.discount_flat")}</option>
                        <option value="bulk">{t("owner.court_form.discount_bulk")}</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-2 block">
                        {form.type === "percent" ? t("owner.court_form.discount_percent_value") : t("owner.court_form.discount_flat_value")}
                      </label>
                      <input type="number" min={0} max={form.type === "percent" ? 100 : undefined} value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                    </div>
                    {form.apply_mode === "promo" && (
                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.stack_with_discounts !== false}
                            onChange={(e) => setForm({ ...form, stack_with_discounts: e.target.checked })}
                            className="accent-volt w-4 h-4"
                          />
                          <div>
                            <div className="text-sm text-foreground/80">{t("owner.court_form.discount_stack")}</div>
                            <div className="text-xs text-muted-foreground/80 mt-0.5">{t("owner.court_form.discount_stack_desc")}</div>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section: Bulk / Multi-Hour */}
              {form.type === "bulk" && (
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-4 font-bold">{t("owner.court_form.discount_bulk_settings")}</div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_min_hours")}</label>
                        <input type="number" min={1} value={form.min_hours ?? ""} onChange={(e) => setForm({ ...form, min_hours: e.target.value ? parseInt(e.target.value) : undefined })} placeholder={t("owner.court_form.discount_min_hours_placeholder")} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_bulk_percent")}</label>
                        <input type="number" min={0} max={100} value={form.type === "bulk" ? (form.value || 0) : 0} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                      </div>
                    </div>
                    <div className="border-t border-border pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-muted-foreground font-bold tracking-widest uppercase">{t("owner.court_form.discount_tiers")}</span>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, bulk_tiers: [...(form.bulk_tiers || []), { min_hours: (form.bulk_tiers?.length || 0) + 2, percent: 5 }] })}
                          className="btn-outline rounded-sm px-3 py-1.5 text-xs tracking-widest uppercase font-bold"
                        >
                          + {t("owner.court_form.discount_add_tier")}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground/60 mb-3">{t("owner.court_form.discount_tiers_hint")}</p>
                      {(form.bulk_tiers || []).map((tier, i) => (
                        <div key={i} className="flex items-center gap-3 mb-2">
                          <input
                            type="number" min={1} placeholder={t("owner.court_form.discount_tier_hours")}
                            value={tier.min_hours}
                            onChange={(e) => {
                              const next = [...(form.bulk_tiers || [])];
                              next[i] = { ...next[i], min_hours: parseInt(e.target.value) || 0 };
                              setForm({ ...form, bulk_tiers: next });
                            }}
                            className="w-28 bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
                          />
                          <span className="text-muted-foreground text-sm">{t("owner.court_form.discount_tier_hours_suffix")}</span>
                          <input
                            type="number" min={0} max={100} placeholder="%"
                            value={tier.percent}
                            onChange={(e) => {
                              const next = [...(form.bulk_tiers || [])];
                              next[i] = { ...next[i], percent: parseFloat(e.target.value) || 0 };
                              setForm({ ...form, bulk_tiers: next });
                            }}
                            className="w-24 bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
                          />
                          <span className="text-muted-foreground text-sm">% {t("owner.court_form.discount_off")}</span>
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, bulk_tiers: (form.bulk_tiers || []).filter((_, j) => j !== i) })}
                            className="text-red-400 hover:text-red-300 ml-auto text-xs tracking-widest uppercase font-bold"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Section: Schedule */}
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-4 font-bold">{t("owner.discount_schedule")}</div>
                <div className="space-y-6">
                  <div>
                    <label className="text-xs text-muted-foreground mb-3 block">{t("owner.court_form.discount_days")}</label>
                    <div className="flex gap-2 flex-wrap">
                      {DAY_KEYS.map((dk, di) => (
                        <button
                          key={dk}
                          type="button"
                          onClick={() => setForm({ ...form, days: form.days.includes(di) ? form.days.filter((d) => d !== di) : [...form.days, di] })}
                          className={`px-5 py-2.5 text-sm tracking-widest uppercase font-bold border transition-all ${
                            form.days.includes(di) ? "bg-volt text-primary-foreground border-volt" : "border-border text-muted-foreground hover:border-foreground/30 bg-background"
                          }`}
                        >
                          {t(`calendar.${dk}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-3 block">{t("owner.court_form.discount_time_range")}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {Array.from({ length: 24 }, (_, h) => {
                        const inRange = form.time_from !== undefined && form.time_to !== undefined && h >= form.time_from && h < form.time_to;
                        const isStart = form.time_from === h;
                        const isEnd = form.time_to !== undefined && form.time_to === h + 1;
                        return (
                          <button
                            key={h}
                            type="button"
                            onClick={() => {
                              if (form.time_from === undefined || form.time_to !== undefined) {
                                setForm({ ...form, time_from: h, time_to: undefined });
                              } else if (h <= form.time_from) {
                                setForm({ ...form, time_from: h, time_to: undefined });
                              } else {
                                setForm({ ...form, time_to: h + 1 });
                              }
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setForm({ ...form, time_from: undefined, time_to: undefined });
                            }}
                            className={`w-9 h-9 text-xs font-mono font-bold border transition-all ${
                              inRange
                                ? isStart || isEnd
                                  ? "bg-volt text-primary-foreground border-volt"
                                  : "bg-volt/15 text-volt border-volt/30"
                                : "border-border text-muted-foreground/80 hover:border-foreground/30 bg-background"
                            }`}
                          >
                            {h}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-sm text-muted-foreground">
                      {form.time_from !== undefined ? (
                        <span>{String(form.time_from).padStart(2, "0")}:00 {form.time_to !== undefined ? `– ${String(form.time_to).padStart(2, "0")}:00` : t("owner.court_form.discount_select_end")}</span>
                      ) : (
                        <span className="text-muted-foreground/60 italic">{t("owner.court_form.discount_any")}</span>
                      )}
                      {(form.time_from !== undefined || form.time_to !== undefined) && (
                        <button
                          onClick={() => setForm({ ...form, time_from: undefined, time_to: undefined })}
                          className="text-yellow-400 hover:underline text-xs tracking-widest uppercase"
                        >
                          {t("common.clear")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section: Active Period */}
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-4 font-bold">{t("owner.discount_active_period")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_starts")}</label>
                    <input type="datetime-local" value={form.starts_at || ""} onChange={(e) => setForm({ ...form, starts_at: e.target.value || undefined })} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_expires")}</label>
                    <input type="datetime-local" value={form.expires_at || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value || undefined })} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                  </div>
                </div>
              </div>

              {/* Section: Usage Limits */}
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-4 font-bold">{t("owner.discount_limits")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_max_uses")}</label>
                    <input type="number" min={1} value={form.max_uses ?? ""} onChange={(e) => setForm({ ...form, max_uses: e.target.value ? parseInt(e.target.value) : undefined })} placeholder={t("owner.court_form.discount_unlimited")} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">{t("owner.court_form.discount_per_user")}</label>
                    <input type="number" min={1} value={form.max_uses_per_user ?? ""} onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value ? parseInt(e.target.value) : undefined })} placeholder={t("owner.court_form.discount_unlimited")} className="w-full bg-background border border-border px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                  </div>
                </div>
              </div>

              {/* Section: Courts */}
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-4 font-bold">{t("owner.discount_applies_to")}</div>
                <div className="flex gap-2 flex-wrap">
                  {courts.filter((c) => c.active).length === 0 ? (
                    <span className="text-sm text-muted-foreground/60 italic">{t("owner.no_courts")}</span>
                  ) : (
                    courts.filter((c) => c.active).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setForm({ ...form, court_ids: form.court_ids.includes(c.id) ? form.court_ids.filter((id) => id !== c.id) : [...form.court_ids, c.id] })}
                        className={`px-4 py-2.5 text-sm tracking-widest uppercase font-bold border transition-all ${
                          form.court_ids.includes(c.id)
                            ? "bg-volt text-primary-foreground border-volt"
                            : "border-border text-muted-foreground hover:border-foreground/30 bg-background"
                        }`}
                      >
                        {c.name}
                      </button>
                    ))
                  )}
                </div>
                {form.court_ids.length === 0 && (
                  <p className="text-sm text-muted-foreground/60 mt-3 italic">{t("owner.discount_all_courts_hint")}</p>
                )}
              </div>
            </div>

            <div className="flex gap-4 mt-10 pt-8 border-t border-border">
              <button onClick={cancelEdit} className="btn-outline rounded-sm px-8 py-3.5 text-sm flex-1">{t("common.cancel")}</button>
              <button onClick={save} disabled={saving} className="btn-volt rounded-sm px-8 py-3.5 text-sm flex-1">{saving ? t("common.loading") : t("common.save")}</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-6">
            <div className="label-eyebrow text-volt">{t("owner.discounts_tab")}</div>
            <button onClick={startCreate} className="btn-volt rounded-sm px-4 py-2 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1">
              <Plus className="h-3 w-3" /> {t("owner.court_form.add_discount")}
            </button>
          </div>

          {discounts.length === 0 ? (
            <div className="border border-dashed border-border/30 p-16 text-center">
              <Tag className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground/80 text-sm">{t("owner.no_discounts")}</p>
              <button
                onClick={startCreate}
                className="btn-volt rounded-sm px-5 py-3 text-xs flex items-center gap-2 mx-auto mt-4"
              >
                <Plus className="h-4 w-4" /> {t("owner.court_form.add_discount")}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {discounts.map((d) => {
                const used = d.used_count || 0;
                const cap = d.max_uses;
                return (
                  <div key={d.id} className={`border ${d.active ? "border-border" : "border-border/30"} ${d.active ? "bg-card" : "bg-card/50"} p-5 md:p-6`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-3">
                          <span className={`text-lg font-semibold ${!d.active ? "text-muted-foreground/80" : ""}`}>{d.label || t("owner.discount_unnamed")}</span>
                          {d.code && (
                            <span className="flex items-center gap-1.5 text-xs tracking-widest uppercase px-2 py-1 border font-mono font-bold bg-background">
                              <span className={d.active ? "text-volt" : "text-muted-foreground/60"}>{d.code}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(d.code); toast.success(t("common.copied")); }}
                                className="text-muted-foreground/60 hover:text-foreground transition-colors"
                                title={t("common.copy")}
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </span>
                          )}
                          <span className={`text-xs tracking-widest uppercase px-2 py-1 border font-bold ${d.apply_mode === "promo" ? "border-yellow-400/40 text-yellow-400" : "border-volt/30 text-volt"}`}>
                            {d.apply_mode === "promo" ? t("owner.court_form.discount_promo") : t("owner.court_form.discount_auto")}
                          </span>
                          {!d.active && (
                            <span className="text-xs tracking-widest uppercase px-2 py-1 border border-border text-muted-foreground/80 font-bold">{t("owner.court_form.discount_paused")}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                          <span className="text-volt font-bold text-base">
                            {d.type === "bulk"
                              ? (d.bulk_tiers && d.bulk_tiers.length > 0
                                  ? d.bulk_tiers.map((t) => `${t.min_hours}h → ${t.percent}%`).join(", ")
                                  : `${d.min_hours || "?"}+h → ${d.value}%`)
                              : d.type === "percent" ? `${d.value}%` : formatPrice(d.value, "JOD")}
                            {" "}{t("owner.discount_off")}
                          </span>
                          <span>{d.days.length > 0 ? `${t(`calendar.${DAY_KEYS[d.days[0]]}`)}${d.days.length > 1 ? ` +${d.days.length - 1}` : ""}` : t("owner.court_form.discount_any")}</span>
                          {d.time_from !== undefined && <span>{String(d.time_from).padStart(2, "0")}:00 – {d.time_to !== undefined ? `${String(d.time_to).padStart(2, "0")}:00` : "..."}</span>}
                          {cap !== undefined && <span>{used}/{cap} {t("owner.discount_used")}</span>}
                          {d.max_uses_per_user && <span>{d.max_uses_per_user}x {t("owner.court_form.discount_per_user_label")}</span>}
                          {d.starts_at && <span>{t("owner.discount_starts")} {d.starts_at.replace("T", " ")}</span>}
                          {d.expires_at && <span>{t("owner.discount_expires")} {d.expires_at.replace("T", " ")}</span>}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(d.court_ids || []).length > 0 ? d.court_ids.map((cid) => {
                            const court = courts.find((c) => c.id === cid);
                            return court ? (
                              <span key={cid} className={`text-xs tracking-widest uppercase px-2.5 py-1 border ${d.active ? "border-border text-muted-foreground" : "border-border/30 text-muted-foreground/60"}`}>{court.name}</span>
                            ) : null;
                          }) : (
                            <span className={`text-xs tracking-widest uppercase ${d.active ? "text-muted-foreground/60" : "text-muted-foreground/40"}`}>{t("owner.discount_all_courts")}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => toggleActive(d)}
                          className={`btn-outline rounded-sm px-3 py-2 text-xs flex items-center gap-1.5 ${
                            d.active ? "hover:!border-yellow-400 hover:!text-yellow-400" : "hover:!border-emerald-400 hover:!text-emerald-400"
                          }`}
                        >
                          {d.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                          {d.active ? t("owner.pause") : t("owner.resume")}
                        </button>
                        <button onClick={() => startEdit(d)} className="btn-outline rounded-sm px-3 py-2 text-xs">{t("owner.edit")}</button>
                         <button onClick={() => duplicate(d)} className="btn-outline rounded-sm px-3 py-2 text-xs flex items-center gap-1.5"><Files className="h-3.5 w-3.5" /> {t("common.duplicate")}</button>
                         <button onClick={() => remove(d.id)} className="btn-outline rounded-sm px-3 py-2 text-xs hover:!border-red-400 hover:!text-red-400">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
